import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";

const KEY = "gw-intro-seen";
let ready = true;
const subs = new Set();
const setReady = () => {
  ready = true;
  subs.forEach((f) => f(true));
};

const shouldPlay = () => {
  try {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    if (sessionStorage.getItem(KEY)) return false;
    sessionStorage.setItem(KEY, "1");
  } catch {
    return false;
  }
  return window.location.pathname === "/";
};
const PLAY = typeof window !== "undefined" && shouldPlay();
if (PLAY) ready = false;

export const useIntroReady = () => {
  const [r, setR] = useState(ready);
  useEffect(() => {
    subs.add(setR);
    return () => { subs.delete(setR); };
  }, []);
  return r;
};

const geo = (vw, vh) => {
  const land = vw >= vh;
  const n = land ? 4 : 3;
  const L = land ? vw : vh;
  const T = (land ? vh : vw) / n;
  const th = T * 0.98;
  return { land, n, L, T, th, M: th * 3.21, vw, vh };
};
const dirOf = (i) => (i % 2 === 0 ? 1 : -1);
const centerAt = (g, i, v) => (dirOf(i) > 0 ? -g.M / 2 + v * (g.L + g.M) : g.L + g.M / 2 - v * (g.L + g.M));
const edgeAt = (g, i, v) => Math.min(g.L, Math.max(0, centerAt(g, i, v) + dirOf(i) * 0.18 * g.M));
const toXY = (g, along, cross) => (g.land ? [along, cross] : [cross, along]);
const BLADES = ["#7DB247", "#A6D46A", "#5C9A35", "#C9E59A"];

const Lane = ({ i, g, lane, p }) => {
  const clip = useTransform([lane, p], ([l, v]) => {
    if (i < l) return "inset(0 0 100% 0)";
    if (i > l) return "inset(0 0 0 0)";
    const e = edgeAt(g, i, v);
    const fwd = dirOf(i) > 0;
    if (g.land) return fwd ? `inset(0 0 0 ${e}px)` : `inset(0 ${g.L - e}px 0 0)`;
    return fwd ? `inset(${e}px 0 0 0)` : `inset(0 0 ${g.L - e}px 0)`;
  });
  const [left, top] = g.land ? [0, i * g.T] : [i * g.T, 0];
  const [w, h] = g.land ? [g.vw, g.T + 1] : [g.T + 1, g.vh];
  return (
    <motion.div className="absolute overflow-hidden" style={{ left, top, width: w, height: h, clipPath: clip }}>
      <img src="/intro/lawn-top.jpg" alt="" className="absolute max-w-none object-cover" style={{ left: -left, top: -top, width: g.vw, height: g.vh }} />
    </motion.div>
  );
};

const Clipping = ({ c, onDone }) => (
  <motion.span
    className="absolute left-0 top-0 block h-4 w-1 rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
    style={{ background: c.color }}
    initial={{ x: c.x, y: c.y, rotate: c.r, opacity: 1, scale: 1.2 }}
    animate={{ x: c.x + c.dx, y: c.y + c.dy, rotate: c.r + c.spin, opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.85, ease: [0.2, 0.7, 0.4, 1] }}
    onAnimationComplete={() => onDone(c.id)}
  />
);

const rand = (a, b) => a + Math.random() * (b - a);

export const Intro = () => {
  const [show, setShow] = useState(PLAY);
  const [g, setG] = useState(() => geo(window.innerWidth, window.innerHeight));
  const [bits, setBits] = useState([]);
  const lane = useMotionValue(0);
  const p = useMotionValue(0);
  const skipped = useRef(false);
  const mx = useTransform([lane, p], ([l, v]) => toXY(g, centerAt(g, l, v), l * g.T + g.T / 2)[0] - g.M / 2);
  const my = useTransform([lane, p], ([l, v]) => toXY(g, centerAt(g, l, v), l * g.T + g.T / 2)[1] - g.th / 2);
  const rot = useTransform(lane, (l) => (g.land ? (dirOf(l) > 0 ? 0 : 180) : dirOf(l) > 0 ? 90 : -90));

  const finish = () => {
    document.documentElement.style.overflow = "";
    window.__lenis?.start();
    setReady();
    setShow(false);
  };

  useEffect(() => {
    if (!PLAY) return undefined;
    let stopped = false;
    let ctrl;
    const onResize = () => setG(geo(window.innerWidth, window.innerHeight));
    window.addEventListener("resize", onResize);
    document.documentElement.style.overflow = "hidden";
    const lock = requestAnimationFrame(() => window.__lenis?.stop());
    const run = async () => {
      await new Promise((r) => setTimeout(r, 400));
      for (let i = 0; i < g.n; i += 1) {
        if (stopped || skipped.current) return;
        lane.set(i);
        p.set(0);
        ctrl = animate(p, 1, { duration: g.land ? 0.9 : 1, ease: [0.37, 0, 0.63, 1] });
        await ctrl;
      }
      if (!stopped && !skipped.current) finish();
    };
    run();
    return () => {
      stopped = true;
      ctrl?.stop();
      cancelAnimationFrame(lock);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    let id = 0;
    const t = setInterval(() => {
      const l = lane.get();
      const v = p.get();
      if (v <= 0 || v >= 1) return;
      const d = dirOf(l);
      const along = edgeAt(g, l, v) - d * 0.08 * g.M;
      const fresh = Array.from({ length: 3 }, () => {
        const [x, y] = toXY(g, along, l * g.T + g.T / 2 + rand(-0.42, 0.42) * g.T);
        const [dx, dy] = toXY(g, -d * rand(40, 170), rand(-90, 90));
        id += 1;
        return { id: `${l}-${id}`, x, y, dx, dy, r: rand(0, 180), spin: rand(-240, 240), color: BLADES[id % BLADES.length] };
      });
      setBits((b) => [...b.slice(-70), ...fresh]);
    }, 45);
    return () => clearInterval(t);
  }, [show, g, lane, p]);

  const drop = (bid) => setBits((b) => b.filter((x) => x.id !== bid));

  if (!show) return null;
  return (
    <div data-testid="intro-overlay" className="fixed inset-0 z-[100] overflow-hidden" aria-hidden="true">
      {Array.from({ length: g.n }, (_, i) => <Lane key={i} i={i} g={g} lane={lane} p={p} />)}
      {bits.map((c) => <Clipping key={c.id} c={c} onDone={drop} />)}
      <motion.img
        src="/intro/mower-top.png"
        alt=""
        className="mower-rumble absolute left-0 top-0 max-w-none drop-shadow-[0_18px_22px_rgba(0,0,0,0.45)]"
        style={{ x: mx, y: my, rotate: rot, width: g.M, height: g.th }}
      />
      <button
        data-testid="intro-skip-button"
        onClick={() => { skipped.current = true; p.stop(); finish(); }}
        className="absolute bottom-6 right-6 rounded-full bg-[#0D2F18]/70 px-5 py-2.5 text-sm font-medium text-[#FBF9F4] backdrop-blur-md transition-colors hover:bg-[#0D2F18]"
        aria-hidden="false"
      >
        Überspringen
      </button>
    </div>
  );
};
